import React, { useState, useEffect, useContext } from 'react';
import promptService from '../services/promptService';
import { AuthContext } from '../contexts/AuthContext';
import { formatDate } from '../utils/dateUtils';

const PromptManager = () => {
    const { hasPermission } = useContext(AuthContext);
    const hasPromptAdminPermission = hasPermission('prompt_admin');
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showVersionsModal, setShowVersionsModal] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    const [promptVersions, setPromptVersions] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        system_prompt: '',
        prompt: '',
        output_format: '',
        model_id: 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
        temperature: 0.0,
        top_p: 1.0,
        top_k: 250,
        caching: ['Tools', 'System instructions', 'Messages'],
        reasoning_enabled: false,
        reasoning_budget_tokens: 8192,
        version_type: 'minor',  // Default to minor version update
        owner_type: hasPromptAdminPermission ? 'client' : 'client'    // Default to client owned for new prompts
    });
    const [submitting, setSubmitting] = useState(false);
    const [selectedTemplatePrompt, setSelectedTemplatePrompt] = useState('');

    // Search and sorting state
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    // Expandable rows state
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Model configuration collapse state
    const [showModelConfig, setShowModelConfig] = useState(false);

    // Helper function to check if model is Claude Sonnet 4.5
    const isClaude45Sonnet = (modelId) => {
        return modelId && (modelId.includes('claude-sonnet-4-6') || modelId.includes('claude-sonnet-4-5') || modelId.includes('claude-haiku-4-5'));
    };

    const isSonnet5 = (modelId) => {
        return !!modelId && modelId.includes('claude-sonnet-5');
    };

    // The two aliases below gate different parts of the form and read as two
    // distinct capabilities at their call sites. They happen to share one rule
    // today; keeping the names separate lets them diverge if a later model
    // splits the behaviours.

    // Sonnet 5+ rejects non-default sampling parameters, so the backend omits
    // temperature/top_p/top_k entirely. Don't offer controls whose values would
    // be silently discarded.
    const ignoresSamplingParams = isSonnet5;
    const allowsTemperature = (modelId) => !ignoresSamplingParams(modelId);
    const allowsTopK = (modelId) => !ignoresSamplingParams(modelId);

    const supportsReasoning = (modelId) => {
        return !!modelId && (
            modelId.includes('claude-sonnet-4') ||
            modelId.includes('claude-haiku-4-5') ||
            modelId.includes('claude-sonnet-5')
        );
    };

    // Sonnet 5+ uses adaptive thinking, which has no token budget to configure.
    const usesAdaptiveThinking = isSonnet5;

    // Fetch prompts on mount
    useEffect(() => {
        fetchPrompts();
    }, []);

    const fetchPrompts = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await promptService.getPrompts();
            if (response.success) {
                setPrompts(response.prompts || []);
            } else {
                setError('Failed to fetch prompts');
            }
        } catch (err) {
            setError(`Failed to fetch prompts: ${err.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to calculate next version
    const getNextVersion = (currentVersion, versionType) => {
        try {
            const [major, minor] = currentVersion.includes('.')
                ? currentVersion.split('.').map(Number)
                : [parseInt(currentVersion), 0]; // Handle legacy single number versions

            if (versionType === 'major') {
                return `${major + 1}.0`;
            } else {
                return `${major}.${minor + 1}`;
            }
        } catch (error) {
            return 'X.X';
        }
    };

    const handleTemplatePromptSelection = (promptId) => {
        if (!promptId) {
            // Reset to default empty form if no template selected
            setFormData({
                name: '',
                description: '',
                system_prompt: '',
                prompt: '',
                output_format: '',
                model_id: 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
                temperature: 0.0,
                top_p: 1.0,
                top_k: 250,
                caching: ['Tools', 'System instructions', 'Messages'],
                reasoning_enabled: false,
                reasoning_budget_tokens: 8192,
                version_type: 'minor',
                owner_type: hasPromptAdminPermission ? 'client' : 'client'
            });
            setSelectedTemplatePrompt('');
            return;
        }

        const selectedPrompt = prompts.find(p => p.prompt_id === promptId);
        if (selectedPrompt) {
            setFormData({
                name: '', // Keep name empty so user can set their own
                description: selectedPrompt.description || '',
                system_prompt: selectedPrompt.system_prompt || '',
                prompt: selectedPrompt.prompt || '',
                output_format: selectedPrompt.output_format || '',
                model_id: selectedPrompt.model_id || 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
                // ?? not || : 0 is a legal value for temperature and top_p.
                temperature: selectedPrompt.temperature ?? 0.0,
                top_p: selectedPrompt.top_p ?? 1.0,
                top_k: selectedPrompt.top_k || 250,
                caching: selectedPrompt.caching || ['Tools', 'System instructions', 'Messages'],
                reasoning_enabled: selectedPrompt.reasoning_enabled || false,
                reasoning_budget_tokens: selectedPrompt.reasoning_budget_tokens || 8192,
                version_type: 'minor',
                // Only admins can set owner_type from template, regular users always get 'client'
                owner_type: hasPromptAdminPermission ? (selectedPrompt.owner_type || 'client') : 'client'
            });
            setSelectedTemplatePrompt(promptId);
        }
    };

    const handleCreatePrompt = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.prompt.trim() || !formData.description.trim()) {
            setError('Name, description, and prompt text are required');
            return;
        }

        // Debug logging to see what we're sending
        console.log('Creating prompt with data:', {
            name: formData.name,
            prompt: formData.prompt,
            prompt_length: formData.prompt.length,
            owner_type: formData.owner_type,
            app_id: formData.name  // Always set app_id to name
        });

        try {
            setSubmitting(true);
            setError('');
            const response = await promptService.createPrompt({
                name: formData.name.trim(),
                description: formData.description.trim(),
                system_prompt: formData.system_prompt.trim(),
                prompt: formData.prompt.trim(),
                output_format: formData.output_format.trim() || null,
                model_id: formData.model_id,
                temperature: parseFloat(formData.temperature),
                top_p: parseFloat(formData.top_p),
                top_k: parseInt(formData.top_k),
                caching: formData.caching,
                reasoning_enabled: formData.reasoning_enabled,
                reasoning_budget_tokens: parseInt(formData.reasoning_budget_tokens),
                owner_type: hasPromptAdminPermission ? formData.owner_type : 'client',  // Force client owned for non-admin users
                app_id: formData.name.trim()  // Always set app_id to name
            });

            if (response.success) {
                setShowCreateModal(false);
                setFormData({
                    name: '',
                    description: '',
                    system_prompt: '',
                    prompt: '',
                    output_format: '',
                    model_id: 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
                    temperature: 0.0,
                    top_p: 1.0,
                    top_k: 250,
                    caching: ['Tools', 'System instructions', 'Messages'],
                    reasoning_enabled: false,
                    reasoning_budget_tokens: 8192,
                    version_type: 'minor',
                    owner_type: 'client'
                });
                await fetchPrompts();
            } else {
                setError('Failed to create prompt');
            }
        } catch (err) {
            setError(`Failed to create prompt: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditPrompt = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !formData.prompt.trim() || !formData.description.trim()) {
            setError('Name, description, and prompt text are required');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            const response = await promptService.updatePrompt(selectedPrompt.prompt_id, {
                name: formData.name.trim(),
                description: formData.description.trim(),
                system_prompt: formData.system_prompt.trim(),
                prompt: formData.prompt.trim(),
                output_format: formData.output_format.trim() || null,
                model_id: formData.model_id,
                temperature: parseFloat(formData.temperature),
                top_p: parseFloat(formData.top_p),
                top_k: parseInt(formData.top_k),
                caching: formData.caching,
                reasoning_enabled: formData.reasoning_enabled,
                reasoning_budget_tokens: parseInt(formData.reasoning_budget_tokens),
                version_type: formData.version_type,
                app_id: formData.name.trim()  // Always set app_id to name
            });

            if (response.success) {
                setShowEditModal(false);
                setSelectedPrompt(null);
                setFormData({
                    name: '',
                    description: '',
                    system_prompt: '',
                    prompt: '',
                    output_format: '',
                    model_id: 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
                    temperature: 0.0,
                    top_p: 1.0,
                    top_k: 250,
                    caching: ['Tools', 'System instructions', 'Messages'],
                    reasoning_enabled: false,
                    reasoning_budget_tokens: 8192,
                    version_type: 'minor',
                    owner_type: 'client'
                });
                await fetchPrompts();
            } else {
                setError('Failed to update prompt');
            }
        } catch (err) {
            setError(`Failed to update prompt: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeletePrompt = async () => {
        try {
            setSubmitting(true);
            setError('');
            const response = await promptService.deletePrompt(selectedPrompt.prompt_id);

            if (response.success) {
                setShowDeleteModal(false);
                setSelectedPrompt(null);
                await fetchPrompts();
            } else {
                setError('Failed to delete prompt');
            }
        } catch (err) {
            setError(`Failed to delete prompt: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleSetDefaultVersion = async (version) => {
        // Prevent setting default version for app-owned prompts if user doesn't have admin permission
        if (selectedPrompt?.owner_type === 'app' && !hasPromptAdminPermission) {
            setError('Cannot set default version for app-owned prompts. Admin permission required.');
            return;
        }

        try {
            setError('');
            const response = await promptService.setDefaultVersion(version.guid, version.version);

            if (response.success) {
                // Refresh the versions list to show updated default status
                const versionsResponse = await promptService.getPromptVersions(version.guid);
                if (versionsResponse.success) {
                    setPromptVersions(versionsResponse.prompts || []);
                }

                // Also refresh the main prompts list to show updated default indicator
                await fetchPrompts();
            } else {
                setError('Failed to set default version');
            }
        } catch (err) {
            setError(`Failed to set default version: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handleViewVersions = async (prompt) => {
        try {
            setError('');
            const response = await promptService.getPromptVersions(prompt.prompt_id);

            if (response.success) {
                setPromptVersions(response.prompts || []);
                setSelectedPrompt(prompt);
                setShowVersionsModal(true);
            } else {
                setError('Failed to fetch prompt versions');
            }
        } catch (err) {
            setError(`Failed to fetch versions: ${err.response?.data?.detail || err.message}`);
        }
    };

    const openCreateModal = () => {
        setFormData({
            name: '',
            description: '',
            system_prompt: '',
            prompt: '',
            output_format: '',
            model_id: 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
            temperature: 0.0,
            top_p: 1.0,
            top_k: 250,
            caching: ['Tools', 'System instructions', 'Messages'],
            reasoning_enabled: false,
            reasoning_budget_tokens: 8192,
            version_type: 'minor',
            owner_type: 'client'
        });
        setSelectedTemplatePrompt('');
        setError('');
        setShowCreateModal(true);
    };

    const openEditModal = (prompt) => {
        setSelectedPrompt(prompt);
        setFormData({
            name: prompt.name,
            description: prompt.description || '',
            system_prompt: prompt.system_prompt || '',
            prompt: prompt.prompt,
            output_format: prompt.output_format || '',
            model_id: prompt.model_id || 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
            // ?? not || : 0 is a legal value for temperature and top_p, so it must
            // survive into the form rather than being replaced by the default.
            temperature: prompt.temperature ?? 0.0,
            top_p: prompt.top_p ?? 1.0,
            top_k: prompt.top_k || 250,
            caching: prompt.caching || ['Tools', 'System instructions', 'Messages'],
            reasoning_enabled: prompt.reasoning_enabled || false,
            reasoning_budget_tokens: prompt.reasoning_budget_tokens || 8192,
            version_type: 'minor',  // Default to minor version
            owner_type: prompt.owner_type || 'client'  // Include owner_type
        });
        setError('');
        setShowEditModal(true);
    };

    const openDeleteModal = (prompt) => {
        // Prevent opening delete modal for app-owned prompts if user doesn't have admin permission
        if (prompt.owner_type === 'app' && !hasPromptAdminPermission) {
            setError('Cannot delete app-owned prompts. Admin permission required.');
            return;
        }
        setSelectedPrompt(prompt);
        setError('');
        setShowDeleteModal(true);
    };

    const closeModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        setShowVersionsModal(false);
        setSelectedPrompt(null);
        setPromptVersions([]);
        setSelectedTemplatePrompt('');
        setFormData({
            name: '',
            description: '',
            system_prompt: '',
            prompt: '',
            output_format: '',
            model_id: 'arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
            temperature: 0.0,
            top_p: 1.0,
            top_k: 250,
            caching: ['Tools', 'System instructions', 'Messages'],
            reasoning_enabled: false,
            reasoning_budget_tokens: 8192,
            version_type: 'minor',
            owner_type: 'client'
        });
        setError('');
    };

    // Search and sorting functions
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedPrompts = React.useMemo(() => {
        let filtered = prompts.filter(prompt =>
            prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            prompt.description.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'updated_at' || sortConfig.key === 'created_at') {
                    aValue = new Date(aValue);
                    bValue = new Date(bValue);
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return filtered;
    }, [prompts, searchTerm, sortConfig]);

    const getSortIcon = (columnKey) => {
        if (sortConfig.key === columnKey) {
            return sortConfig.direction === 'asc' ? '↑' : '↓';
        }
        return '↕';
    };

    const toggleRowExpansion = (promptId) => {
        setExpandedRows(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(promptId)) {
                newExpanded.delete(promptId);
            } else {
                newExpanded.add(promptId);
            }
            return newExpanded;
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>Prompt Manager</h2>
                <button className="btn btn-primary" onClick={openCreateModal}>
                    <i className="bi bi-plus-lg me-2"></i>Create New Prompt
                </button>
            </div>

            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                </div>
            )}

            {/* Search and Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <i className="bi bi-search"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search prompts by name or description..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-6 text-end">
                            <span className="text-muted">
                                Showing {filteredAndSortedPrompts.length} of {prompts.length} prompts
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Prompts Table */}
            <div className="card">
                <div className="card-body">
                    {filteredAndSortedPrompts.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="bi bi-file-text fa-3x text-muted mb-3"></i>
                            <p className="text-muted">
                                {searchTerm ? 'No prompts found matching your search.' : 'No prompts created yet.'}
                            </p>
                            {!searchTerm && (
                                <button className="btn btn-primary" onClick={openCreateModal}>
                                    <i className="bi bi-plus-lg me-2"></i>Create Your First Prompt
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th
                                            scope="col"
                                            className="sortable"
                                            onClick={() => handleSort('name')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Name {getSortIcon('name')}
                                        </th>
                                        <th scope="col">Description</th>
                                        <th scope="col">Model</th>
                                        <th
                                            scope="col"
                                            className="sortable"
                                            onClick={() => handleSort('version')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Version {getSortIcon('version')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="sortable"
                                            onClick={() => handleSort('owner_type')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Owner {getSortIcon('owner_type')}
                                        </th>
                                        <th
                                            scope="col"
                                            className="sortable"
                                            onClick={() => handleSort('updated_at')}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            Last Updated {getSortIcon('updated_at')}
                                        </th>
                                        <th scope="col">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAndSortedPrompts.map((prompt) => (
                                        <React.Fragment key={prompt.prompt_id}>
                                            <tr
                                                onClick={() => toggleRowExpansion(prompt.prompt_id)}
                                                style={{ cursor: 'pointer' }}
                                                className="table-row-hover"
                                            >
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <i className={`bi ${expandedRows.has(prompt.prompt_id) ? 'bi-chevron-down' : 'bi-chevron-right'} me-2`}></i>
                                                        <strong>{prompt.name}</strong>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="text-muted">
                                                        {prompt.description ?
                                                            (prompt.description.length > 100 ?
                                                                `${prompt.description.substring(0, 100)}...` :
                                                                prompt.description
                                                            ) :
                                                            'No description'
                                                        }
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary">
                                                        {prompt.model_id ?
                                                            prompt.model_id.includes('claude-sonnet-5') ? 'Claude Sonnet 5' :
                                                            prompt.model_id.includes('claude-sonnet-4-6') ? 'Claude Sonnet 4.6' :
                                                                prompt.model_id.includes('claude-sonnet-4-5') ? 'Claude Sonnet 4.5' :
                                                                    prompt.model_id.includes('claude-haiku-4-5') ? 'Claude Haiku 4.5' :
                                                                        prompt.model_id.includes('claude-sonnet-4') ? 'Claude Sonnet 4' :
                                                                            'Claude' : 'Claude'
                                                        }
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="badge bg-secondary">v{prompt.version}</span>
                                                </td>
                                                <td>
                                                    {hasPromptAdminPermission ? (
                                                        <span className={`badge ${prompt.owner_type === 'app' ? 'bg-warning' : 'bg-info'}`}>
                                                            {prompt.owner_type === 'app' ? 'App Owned' : 'Client Owned'}
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className={`badge ${prompt.owner_type === 'app' ? 'bg-warning' : 'bg-info'}`}
                                                            title={prompt.owner_type === 'app' ? 'App Owned - Limited editing permissions' : 'Client Owned - Full editing permissions'}
                                                        >
                                                            {prompt.owner_type === 'app' ? (
                                                                <>
                                                                    <i className="bi bi-lock me-1"></i>
                                                                    App Owned
                                                                </>
                                                            ) : (
                                                                'Client Owned'
                                                            )}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <small className="text-muted">
                                                        {formatDate(prompt.updated_at)}
                                                    </small>
                                                </td>
                                                <td onClick={(e) => e.stopPropagation()}>
                                                    <div className="btn-group" role="group">
                                                        <button
                                                            className={`btn btn-sm ${(prompt.owner_type === 'app' && !hasPromptAdminPermission) ? 'btn-outline-secondary' : 'btn-primary'}`}
                                                            onClick={() => openEditModal(prompt)}
                                                            title={
                                                                (prompt.owner_type === 'app' && !hasPromptAdminPermission)
                                                                    ? "View Prompt (Read-only - App-owned prompt requires admin permission to edit)"
                                                                    : "Edit Prompt"
                                                            }
                                                        >
                                                            <i className={`bi ${(prompt.owner_type === 'app' && !hasPromptAdminPermission) ? 'bi-eye' : 'bi-pencil'}`}></i>
                                                        </button>
                                                        <button
                                                            className={`btn btn-sm ${(prompt.owner_type === 'app' && !hasPromptAdminPermission) ? 'btn-outline-secondary' : 'btn-info'}`}
                                                            onClick={() => handleViewVersions(prompt)}
                                                            title="View Versions"
                                                        >
                                                            <i className="bi bi-clock-history"></i>
                                                        </button>
                                                        <button
                                                            className={`btn btn-sm ${(prompt.owner_type === 'app' && !hasPromptAdminPermission) ? 'btn-outline-secondary' : 'btn-danger'}`}
                                                            onClick={() => openDeleteModal(prompt)}
                                                            disabled={prompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                            title={
                                                                (prompt.owner_type === 'app' && !hasPromptAdminPermission)
                                                                    ? "Cannot delete app-owned prompt (requires admin permission)"
                                                                    : "Delete Prompt"
                                                            }
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {expandedRows.has(prompt.prompt_id) && (
                                                <tr>
                                                    <td colSpan="7" className="p-0">
                                                        <div className="card border-0 rounded-0">
                                                            <div className="card-body bg-light">
                                                                <div className="row">
                                                                    <div className="col-md-6">
                                                                        <h6 className="text-primary mb-2">
                                                                            <i className="bi bi-gear-fill me-1"></i>
                                                                            System Prompt
                                                                        </h6>
                                                                        {prompt.system_prompt ? (
                                                                            <pre
                                                                                className="bg-white p-3 border rounded small"
                                                                                style={{
                                                                                    whiteSpace: 'pre-wrap',
                                                                                    wordWrap: 'break-word',
                                                                                    maxHeight: '200px',
                                                                                    overflowY: 'auto'
                                                                                }}
                                                                            >
                                                                                {prompt.system_prompt}
                                                                            </pre>
                                                                        ) : (
                                                                            <div className="text-muted fst-italic">No system prompt defined</div>
                                                                        )}
                                                                    </div>
                                                                    <div className="col-md-6">
                                                                        <h6 className="text-success mb-2">
                                                                            <i className="bi bi-chat-dots-fill me-1"></i>
                                                                            User Message Prompt
                                                                        </h6>
                                                                        {prompt.prompt ? (
                                                                            <pre
                                                                                className="bg-white p-3 border rounded small"
                                                                                style={{
                                                                                    whiteSpace: 'pre-wrap',
                                                                                    wordWrap: 'break-word',
                                                                                    maxHeight: '200px',
                                                                                    overflowY: 'auto'
                                                                                }}
                                                                            >
                                                                                {prompt.prompt}
                                                                            </pre>
                                                                        ) : (
                                                                            <div className="text-muted fst-italic">No user prompt defined</div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="mt-3 pt-3 border-top">
                                                                    <div className="row text-sm">
                                                                        <div className="col-md-4">
                                                                            <strong>Temperature:</strong> {prompt.temperature ?? 0.0}
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <strong>Top P:</strong> {prompt.top_p ?? 1.0}
                                                                        </div>
                                                                        <div className="col-md-4">
                                                                            <strong>Top K:</strong> {prompt.top_k ?? 250}
                                                                        </div>
                                                                    </div>
                                                                    <div className="row text-sm mt-2">
                                                                        <div className="col-md-12">
                                                                            <strong>Caching:</strong> {prompt.caching ? prompt.caching.join(', ') : 'None'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Create New Prompt</h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <form onSubmit={handleCreatePrompt}>
                                <div className="modal-body">
                                    {/* Template Selection */}
                                    <div className="mb-3">
                                        <div className="input-group input-group-sm">
                                            <span className="input-group-text">
                                                <i className="bi bi-file-earmark-text me-1"></i>Template (Optional)
                                            </span>
                                            <select
                                                className="form-select"
                                                value={selectedTemplatePrompt}
                                                onChange={(e) => handleTemplatePromptSelection(e.target.value)}
                                            >
                                                <option value="">Start with empty form</option>
                                                {prompts.filter(prompt =>
                                                    // Show all prompts to admins, only client-owned to regular users
                                                    hasPromptAdminPermission || prompt.owner_type !== 'app'
                                                ).map((prompt) => (
                                                    <option key={prompt.prompt_id} value={prompt.prompt_id}>
                                                        {prompt.name} (v{prompt.version})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="name" className="form-label">Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\s/g, '_');
                                                setFormData({ ...formData, name: value });
                                            }}
                                            required
                                            maxLength={255}
                                        />
                                        <div className="form-text">Name cannot contain spaces</div>
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="description" className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            id="description"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            required
                                            maxLength={1000}
                                        />
                                    </div>

                                    {/* Owner Type section - only show if user has prompt_admin permission */}
                                    {hasPromptAdminPermission && (
                                        <div className="mb-3">
                                            <label htmlFor="ownerType" className="form-label">Owner Type *</label>
                                            <select
                                                className="form-select"
                                                id="ownerType"
                                                value={formData.owner_type}
                                                onChange={(e) => setFormData({ ...formData, owner_type: e.target.value })}
                                                required
                                            >
                                                <option value="client">Client Owned</option>
                                                <option value="app">App Owned</option>
                                            </select>
                                            <div className="form-text">
                                                <strong>Client Owned:</strong> Can be freely edited and renamed<br />
                                                <strong>App Owned:</strong> Name is fixed, only content can be updated
                                            </div>
                                        </div>
                                    )}

                                    {/* Model Configuration Section */}
                                    <div className="card mb-3">
                                        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowModelConfig(!showModelConfig)}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">Model Configuration</h6>
                                                <i className={`bi ${showModelConfig ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                            </div>
                                        </div>
                                        <div className={`collapse ${showModelConfig ? 'show' : ''}`}>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className={`${supportsReasoning(formData.model_id) ? 'col-md-6' : 'col-md-12'} mb-3`}>
                                                        <label htmlFor="modelId" className="form-label">Model *</label>
                                                        <select
                                                            className="form-select"
                                                            id="modelId"
                                                            value={formData.model_id}
                                                            onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                                                            required
                                                        >
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0">Claude 4 Sonnet v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-5-20250929-v1:0">Claude 4.5 Sonnet v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-haiku-4-5-20251001-v1:0">Claude 4.5 Haiku v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-6">Claude 4.6 Sonnet v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-5">Claude 5 Sonnet v1</option>
                                                        </select>
                                                    </div>
                                                    {supportsReasoning(formData.model_id) && (
                                                        <div className="col-md-6 mb-3 d-flex flex-column justify-content-center">
                                                            <div className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id="reasoningEnabled"
                                                                    checked={!!formData.reasoning_enabled}
                                                                    onChange={(e) => setFormData({ ...formData, reasoning_enabled: e.target.checked })}
                                                                />
                                                                <label className="form-check-label" htmlFor="reasoningEnabled">
                                                                    Enable Extended Thinking (Reasoning)
                                                                </label>
                                                            </div>
                                                            <div className="form-text">Enables chain-of-thought reasoning on supported models</div>
                                                        </div>
                                                    )}
                                                </div>
                                                {ignoresSamplingParams(formData.model_id) ? (
                                                    <div className="row">
                                                        <div className="col-md-12 mb-3">
                                                            <div className="form-text">
                                                                This model does not accept temperature, Top P or Top K - they are omitted from the request.
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="row">
                                                            {allowsTemperature(formData.model_id) && (
                                                                <div className={`${allowsTopK(formData.model_id) ? 'col-md-6' : 'col-md-12'} mb-3`}>
                                                                    <label htmlFor="temperature" className="form-label">Temperature</label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        id="temperature"
                                                                        value={formData.temperature}
                                                                        onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                                                                        min="0"
                                                                        max="1"
                                                                        step="0.1"
                                                                    />
                                                                    <div className="form-text">Randomness level (0.0-1.0)</div>
                                                                </div>
                                                            )}
                                                            {allowsTopK(formData.model_id) && (
                                                                <div className={`${allowsTemperature(formData.model_id) ? 'col-md-6' : 'col-md-12'} mb-3`}>
                                                                    <label htmlFor="topK" className="form-label">Top K</label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        id="topK"
                                                                        value={formData.top_k}
                                                                        onChange={(e) => setFormData({ ...formData, top_k: e.target.value })}
                                                                        min="1"
                                                                        max="500"
                                                                    />
                                                                    <div className="form-text">Top-k sampling (1-500)</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="row">
                                                            {!isClaude45Sonnet(formData.model_id) && (
                                                                <div className="col-md-12 mb-3">
                                                                    <label htmlFor="topP" className="form-label">Top P</label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        id="topP"
                                                                        value={formData.top_p}
                                                                        onChange={(e) => setFormData({ ...formData, top_p: e.target.value })}
                                                                        min="0"
                                                                        max="1"
                                                                        step="0.1"
                                                                    />
                                                                    <div className="form-text">Nucleus sampling (0.0-1.0)</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                <div className="row">
                                                    {formData.reasoning_enabled && !usesAdaptiveThinking(formData.model_id) && (
                                                        <div className="col-md-12 mb-3">
                                                            <label htmlFor="reasoningBudgetTokens" className="form-label">Reasoning Budget Tokens</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                id="reasoningBudgetTokens"
                                                                value={formData.reasoning_budget_tokens}
                                                                onChange={(e) => setFormData({ ...formData, reasoning_budget_tokens: e.target.value })}
                                                                min="1024"
                                                                max="20000"
                                                                step="512"
                                                            />
                                                            <div className="form-text">Token budget for thinking block (1024–20000)</div>
                                                        </div>
                                                    )}
                                                    {formData.reasoning_enabled && usesAdaptiveThinking(formData.model_id) && (
                                                        <div className="col-md-12 mb-3">
                                                            <div className="form-text">
                                                                This model uses adaptive thinking - it decides how much to think per request, so there is no token budget to set.
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="systemPrompt" className="form-label">System Prompt</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            id="systemPrompt"
                                            rows="4"
                                            value={formData.system_prompt}
                                            onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                                            placeholder="Enter system instructions here (optional)..."
                                            maxLength={5000}
                                        />
                                        <div className="form-text">System instructions to set context and behavior</div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="prompt" className="form-label">User Message Prompt *</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            id="prompt"
                                            rows="8"
                                            value={formData.prompt}
                                            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                                            required
                                            placeholder="Enter your user message prompt text here..."
                                        />
                                        <div className="form-text">The main prompt message sent to the AI. Use &#123;&#123;variable&#125;&#125; to swap out text at runtime</div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="output_format" className="form-label">Output Format</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            id="output_format"
                                            rows="8"
                                            value={formData.output_format}
                                            onChange={(e) => setFormData({ ...formData, output_format: e.target.value })}
                                            placeholder={`Output in XML tags only\n<creditcards>\n    <creditcard>\n        <name>Joe Smith</name>\n        <number>123456789</number>\n    </creditcard>\n    <creditcard>\n        <name>Jane Smith</name>\n        <number>X1234</number>\n    </creditcard>\n    etc...\n</creditcards>`}
                                            maxLength={5000}
                                        />
                                        <div className="form-text">Desired output format structure for the prompt response (optional)</div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                Create Prompt
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedPrompt && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission)
                                        ? `View Prompt: ${selectedPrompt.name}`
                                        : `Edit Prompt: ${selectedPrompt.name}`}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <form onSubmit={handleEditPrompt}>
                                <div className="modal-body">
                                    {(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) && (
                                        <div className="alert alert-warning">
                                            <i className="bi bi-lock me-2"></i>
                                            <strong>Read-Only Mode:</strong> This is an app-owned prompt. You need admin permissions to make changes.
                                        </div>
                                    )}
                                    {!(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) && (
                                        <div className="alert alert-info">
                                            <i className="bi bi-info-circle me-2"></i>
                                            Editing will create a new version of this prompt. Current version: v{selectedPrompt.version}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label htmlFor="editName" className="form-label">
                                            Name *
                                            {selectedPrompt?.owner_type === 'app' && (
                                                <small className="text-warning ms-2">
                                                    <i className="bi bi-lock"></i> App-owned prompt names cannot be changed
                                                </small>
                                            )}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="editName"
                                            value={formData.name}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\s/g, '');
                                                setFormData({ ...formData, name: value });
                                            }}
                                            required
                                            maxLength={255}
                                            disabled={selectedPrompt?.owner_type === 'app'}
                                            style={selectedPrompt?.owner_type === 'app' ? { backgroundColor: '#f8f9fa' } : {}}
                                        />
                                        {selectedPrompt?.owner_type === 'app' ? (
                                            <div className="form-text text-muted">
                                                App-owned prompts have fixed names that cannot be modified.
                                            </div>
                                        ) : (
                                            <div className="form-text">Name cannot contain spaces</div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label htmlFor="editDescription" className="form-label">Description *</label>
                                        <textarea
                                            className="form-control"
                                            id="editDescription"
                                            rows="3"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            required
                                            maxLength={1000}
                                            disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                            style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                        />
                                    </div>

                                    {/* Model Configuration Section */}
                                    <div className="card mb-3">
                                        <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowModelConfig(!showModelConfig)}>
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h6 className="mb-0">Model Configuration</h6>
                                                <i className={`bi ${showModelConfig ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                            </div>
                                        </div>
                                        <div className={`collapse ${showModelConfig ? 'show' : ''}`}>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className={`${supportsReasoning(formData.model_id) ? 'col-md-6' : 'col-md-12'} mb-3`}>
                                                        <label htmlFor="editModelId" className="form-label">Model *</label>
                                                        <select
                                                            className="form-select"
                                                            id="editModelId"
                                                            value={formData.model_id}
                                                            onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                                                            required
                                                            disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                            style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                                        >
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0">Claude 4 Sonnet v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-5-20250929-v1:0">Claude 4.5 Sonnet v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-haiku-4-5-20251001-v1:0">Claude 4.5 Haiku v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-4-6">Claude 4.6 Sonnet v1</option>
                                                            <option value="arn:aws:bedrock:us-east-1:473183365444:inference-profile/us.anthropic.claude-sonnet-5">Claude 5 Sonnet v1</option>
                                                        </select>
                                                    </div>
                                                    {supportsReasoning(formData.model_id) && (
                                                        <div className="col-md-6 mb-3 d-flex flex-column justify-content-center">
                                                            <div className="form-check form-switch">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    id="editReasoningEnabled"
                                                                    checked={!!formData.reasoning_enabled}
                                                                    onChange={(e) => setFormData({ ...formData, reasoning_enabled: e.target.checked })}
                                                                    disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                                />
                                                                <label className="form-check-label" htmlFor="editReasoningEnabled">
                                                                    Enable Extended Thinking (Reasoning)
                                                                </label>
                                                            </div>
                                                            <div className="form-text">Enables chain-of-thought reasoning on supported models</div>
                                                        </div>
                                                    )}
                                                </div>
                                                {ignoresSamplingParams(formData.model_id) ? (
                                                    <div className="row">
                                                        <div className="col-md-12 mb-3">
                                                            <div className="form-text">
                                                                This model does not accept temperature, Top P or Top K - they are omitted from the request.
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="row">
                                                            {allowsTemperature(formData.model_id) && (
                                                                <div className={`${allowsTopK(formData.model_id) ? 'col-md-6' : 'col-md-12'} mb-3`}>
                                                                    <label htmlFor="editTemperature" className="form-label">Temperature</label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        id="editTemperature"
                                                                        value={formData.temperature}
                                                                        onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                                                                        min="0"
                                                                        max="1"
                                                                        step="0.1"
                                                                        disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                                        style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                                                    />
                                                                    <div className="form-text">Randomness level (0.0-1.0)</div>
                                                                </div>
                                                            )}
                                                            {allowsTopK(formData.model_id) && (
                                                                <div className={`${allowsTemperature(formData.model_id) ? 'col-md-6' : 'col-md-12'} mb-3`}>
                                                                    <label htmlFor="editTopK" className="form-label">Top K</label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        id="editTopK"
                                                                        value={formData.top_k}
                                                                        onChange={(e) => setFormData({ ...formData, top_k: e.target.value })}
                                                                        min="1"
                                                                        max="500"
                                                                        disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                                        style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                                                    />
                                                                    <div className="form-text">Top-k sampling (1-500)</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="row">
                                                            {!isClaude45Sonnet(formData.model_id) && (
                                                                <div className="col-md-12 mb-3">
                                                                    <label htmlFor="editTopP" className="form-label">Top P</label>
                                                                    <input
                                                                        type="number"
                                                                        className="form-control"
                                                                        id="editTopP"
                                                                        value={formData.top_p}
                                                                        onChange={(e) => setFormData({ ...formData, top_p: e.target.value })}
                                                                        min="0"
                                                                        max="1"
                                                                        step="0.1"
                                                                        disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                                        style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                                                    />
                                                                    <div className="form-text">Nucleus sampling (0.0-1.0)</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                                <div className="row">
                                                    {formData.reasoning_enabled && usesAdaptiveThinking(formData.model_id) && (
                                                        <div className="col-md-12 mb-3">
                                                            <div className="form-text">
                                                                This model uses adaptive thinking - it decides how much to think per request, so there is no token budget to set.
                                                            </div>
                                                        </div>
                                                    )}
                                                    {formData.reasoning_enabled && !usesAdaptiveThinking(formData.model_id) && (
                                                        <div className="col-md-12 mb-3">
                                                            <label htmlFor="editReasoningBudgetTokens" className="form-label">Reasoning Budget Tokens</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                id="editReasoningBudgetTokens"
                                                                value={formData.reasoning_budget_tokens}
                                                                onChange={(e) => setFormData({ ...formData, reasoning_budget_tokens: e.target.value })}
                                                                min="1024"
                                                                max="20000"
                                                                step="1"
                                                                disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                                style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                                            />
                                                            <div className="form-text">Token budget for thinking block (1024–20000)</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="editSystemPrompt" className="form-label">System Prompt</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            id="editSystemPrompt"
                                            rows="4"
                                            value={formData.system_prompt}
                                            onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                                            placeholder="Enter system instructions here (optional)..."
                                            maxLength={5000}
                                            disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                            style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                        />
                                        <div className="form-text">System instructions to set context and behavior</div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="editPrompt" className="form-label">User Message Prompt *</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            id="editPrompt"
                                            rows="8"
                                            value={formData.prompt}
                                            onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                                            required
                                            disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                            style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                        />
                                        <div className="form-text">The main prompt message sent to the AI</div>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="editOutputFormat" className="form-label">Output Format</label>
                                        <textarea
                                            className="form-control font-monospace"
                                            id="editOutputFormat"
                                            rows="8"
                                            value={formData.output_format}
                                            onChange={(e) => setFormData({ ...formData, output_format: e.target.value })}
                                            placeholder={`Output in XML tags only\n<creditcards>\n    <creditcard>\n        <name>Joe Smith</name>\n        <number>123456789</number>\n    </creditcard>\n    <creditcard>\n        <name>Jane Smith</name>\n        <number>X1234</number>\n    </creditcard>\n    etc...\n</creditcards>`}
                                            maxLength={5000}
                                            disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                            style={(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? { backgroundColor: '#f8f9fa' } : {}}
                                        />
                                        <div className="form-text">Desired output format structure for the prompt response (optional)</div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                        {(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? 'Close' : 'Cancel'}
                                    </button>
                                    {!(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) && (
                                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-save me-2"></i>Update Prompt
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && selectedPrompt && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <div className="modal-body">
                                <div className="alert alert-warning">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    <strong>Warning:</strong> This will permanently delete all versions of the prompt "{selectedPrompt.name}". This action cannot be undone.
                                </div>
                                <p>Are you sure you want to delete this prompt?</p>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={handleDeletePrompt}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-trash me-2"></i>Delete Prompt
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Versions Modal */}
            {showVersionsModal && selectedPrompt && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Version History: {selectedPrompt.name}</h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <div className="modal-body">
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Version</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Model</th>
                                                <th>Config</th>
                                                <th>Updated</th>
                                                <th>Prompt Text</th>
                                                <th>Default</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {promptVersions.map((version) => (
                                                <tr key={`${version.guid}-${version.version}`}>
                                                    <td>
                                                        <span className={`badge ${version.is_default ? 'bg-success' : 'bg-secondary'}`}>
                                                            v{version.version}
                                                            {version.is_default && ' (Default)'}
                                                        </span>
                                                    </td>
                                                    <td>{version.name}</td>
                                                    <td>{version.description || 'No description'}</td>
                                                    <td>
                                                        <span className="badge bg-secondary">
                                                            {version.model_id ?
                                                                version.model_id.includes('claude-sonnet-5') ? 'Claude Sonnet 5' :
                                                                version.model_id.includes('claude-sonnet-4-6') ? 'Claude Sonnet 4.6' :
                                                                    version.model_id.includes('claude-sonnet-4-5') ? 'Claude Sonnet 4.5' :
                                                                        version.model_id.includes('claude-haiku-4-5') ? 'Claude Haiku 4.5' :
                                                                            version.model_id.includes('claude-sonnet-4') ? 'Claude Sonnet 4' :
                                                                                'Claude' : 'Claude'
                                                            }
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            T: {version.temperature ?? 0.0}<br />
                                                            P: {version.top_p ?? 1.0}<br />
                                                            K: {version.top_k ?? 250}
                                                        </small>
                                                    </td>
                                                    <td>
                                                        <small>{formatDate(version.updated_at)}</small>
                                                    </td>
                                                    <td>
                                                        <details>
                                                            <summary className="text-primary" style={{ cursor: 'pointer' }}>
                                                                View prompts
                                                            </summary>
                                                            <div className="mt-2">
                                                                {version.system_prompt && (
                                                                    <div className="mb-2">
                                                                        <strong>System Prompt:</strong>
                                                                        <pre className="mt-1 p-2 bg-light border rounded small">
                                                                            {version.system_prompt}
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                                <strong>User Message:</strong>
                                                                <pre className="mt-1 p-2 bg-light border rounded small">
                                                                    {version.prompt}
                                                                </pre>
                                                            </div>
                                                        </details>
                                                    </td>
                                                    <td>
                                                        {!version.is_default && (
                                                            <button
                                                                className={`btn btn-sm ${(selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission) ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                                                onClick={() => handleSetDefaultVersion(version)}
                                                                disabled={selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission}
                                                                title={
                                                                    (selectedPrompt.owner_type === 'app' && !hasPromptAdminPermission)
                                                                        ? "Cannot set default version for app-owned prompt (requires admin permission)"
                                                                        : "Set as default version"
                                                                }
                                                            >
                                                                <i className="bi bi-star"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PromptManager;